import { Component, ElementRef, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { InvoiceService } from 'src/app/business-service/invoice/invoice.service';
import { LeadsService } from 'src/app/business-service/leads/leads.service';
import { ResponseNotificationService } from 'src/app/modules/shared/services/response-notification/response-notification.service';
import { GlobalService } from '../../shared/services/global/global.service';
import { ComponentReloadService } from '../../shared/services/reload/component-reload.service';
import { Router } from '@angular/router';
import { ItineraryService } from 'src/app/business-service/quote/itinerary.service';
import { TRAVEL_SERVICES } from '../../shared/constants/api-constants';
import { PaymentService } from 'src/app/business-service/payments/payment.service';
import { LovDataService } from '../../shared/services/lov-data/lov-data.service';

@Component({
  selector: 'app-invoice-form',
  templateUrl: './invoice-form.component.html',
  styleUrls: ['./invoice-form.component.scss']
})
export class InvoiceFormComponent implements OnInit {

  @Input() convertingQuote: boolean = false;

  invoiceForm!: FormGroup;
  editInvoiceId: string | null = null;
  invoiceDetails:any;

  leadDetails:any;
  quoteData:any;

  advanceRecieved:boolean = false;

  @Input() leadId: string |null = null;
  @Input() quoteId: any;

  amountReceviedAgainstLead: any = 0;

  currencyLov: any = [];
  defaultCurrency: any;

  constructor(
    private fb: FormBuilder, 
    private modalController: ModalController,
    private _invoiceService: InvoiceService,
    private _responseNotificationService: ResponseNotificationService,
    private _leadService: LeadsService,
    private _globalService: GlobalService,
    private _reloadService: ComponentReloadService,
    private _router: Router,
    private _itinerService: ItineraryService,
    private _paymentService: PaymentService,
    private _lovDataService: LovDataService
  ) {}

  async ngOnInit() {    
    let organizationDetails = await this._globalService.getLoggedInOrgDetails();
    this.defaultCurrency = organizationDetails.organizationDetails.currencyCode;
    this.initializeForm();

    if(this.editInvoiceId){
      this._invoiceService.getInvoiceDetails(this.editInvoiceId).subscribe((data)=>{
        this.invoiceDetails = data;
        this.patchInvoiceData(this.invoiceDetails)
      }, (error)=>{
        this._responseNotificationService.showMessage("Something went wrong, please try again later", true);
      })
    } else {
      const organization = organizationDetails;
      if(organization){
        this.invoiceForm.patchValue({
          ownershipName: organization?.organizationDetails?.name,
          ownershipPhoneNumber: organization?.paymentDetails?.phone,
          ownershipGSTIN: organization?.paymentDetails?.gstin,
          ownershipOfficeEmail: organization?.paymentDetails?.email,

          // Bank related details
          beneficiaryName: organization?.paymentDetails?.accountName,
          accountNumber: organization?.paymentDetails?.accountNumber,
          bankName: organization?.paymentDetails?.bankName,
          ifscCode: organization?.paymentDetails?.ifscCode,
        })
      }
    }

    this.currencyLov = await this.loadLovData("CurrencyCode");

  }

  async loadLovData(category: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this._lovDataService.getLovData(category).subscribe({
        next: (res: any) => {
          resolve(res);
        },
        error: (error: any) => {
          console.error(`Error while fetching LOV data for category ${category}`, error);
          reject(error);
        }
      });
    });
  }

  initializeForm() {
    this.invoiceForm = this.fb.group({
      internalInvoiceId: [''],
      currencyCode: [this.defaultCurrency || 'INR'],
      clientId: ['', Validators.required],
      clientFirstName: ['', Validators.required],
      clientLastName: ['', Validators.required],
      clientPhoneNumber: [{countryCode: '+91', phone: '' }, Validators.required],
      clientEmail: ['', [Validators.email]],
      clientCompanyName: ['', []],
      clientAddress: ['', Validators.required],
      hsnCode: [''],
      gstNumberOfClinet: [''],
      placeOfSupply: ['', Validators.required],

      // Ownerships fields
      ownershipName: [{value: "", disabled: true},Validators.required],
      ownershipPhoneNumber: [null, [Validators.required]],
      ownershipGSTIN: [''],
      ownershipOfficeEmail: ['', [Validators.required, Validators.email]],

      // Payment fields
      beneficiaryName: ['',Validators.required],
      accountNumber: ['',Validators.required],
      bankName: ['',Validators.required],
      ifscCode: ['',Validators.required],
      invoiceDate: [null ,Validators.required],
      dueDate: ['',Validators.required],
      
      // Invoice items and prices
      invoiceItems: this.fb.array([this.createInvoiceItem()]),
      isTcsApplicable: [false],
      isGstOnTotalAmount: [false],
      totalInvoiceItemsAmount: [{value: 0, disabled: true}],
      serviceAmount: [""],
      discountedAmount: [{value: '', disabled: false}, [this.validateDiscountAmount()]],
      totalAmountAfterDiscount: [{value: 0, disabled: true}],
      selectedGstType: ['combinedGst'],
      igstvalue: [0],
      cgstValue: [0],
      sgstValue: [0],
      tcsValue: [0],
      gstAmount: [{value: 0, disabled: true}],
      tcsAmount: [{value: 0, disabled: true}],
      paymentMethod: [''],
      paymentReferenceNumber: [''],
      amountPaid: [{value: '', disabled: false}, [this.validateAdvanceAmount()]],
      netPayable: [{value: 0, disabled: true}],
      balanceAmountInWords: [''],
      cancellationPolicy: [''],
      latePaymentTerms: [''],
      termsAndConditions: [''],
      notes: [''],
    });

    if(this.leadId){
      this._leadService.getLead(this.leadId).subscribe((data)=>{
        this.leadDetails = data;
        this.patchLeadDataToInvoiceItems(this.leadDetails);
        this.patchClientdata(this.leadDetails);
        if(data.itineraryId){
          this.getQuoteData(this.leadDetails.itineraryId);
        }
      })

      if(!this.editInvoiceId){
        this._paymentService.getLatestPaymentOfLead(this.leadId).subscribe({
          next: (res) => {
            if (res?.status === false) {
              return
            }
            this.amountReceviedAgainstLead = res?.updatedBalanceAmount || 0;
            if(this.amountReceviedAgainstLead){
              this._globalService.customAlert(`Please note that an advance payment of ₹${this.amountReceviedAgainstLead} has already been received for this lead. All previous payment records will be linked to this invoice upon submission.`);
            }
          }
        })
      }      
    }

    // Value change subscription
    this.setupValueChangeSubscriptions()
  }

  get todayDate() : Date {
    let date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }

  convertToDate(value:any):Date{
    return new Date(value);
  }

  getQuoteData(quoteId:any){
    this._itinerService.getQuoteData(quoteId).subscribe((data)=>{
      this.quoteData = data;
      this.patchItineraryDetails(this.quoteData)
    })
  }

  patchLeadDataToInvoiceItems(leadDetails:any){    
    const invoiceItems = this.invoiceItems;  
    let items = leadDetails.travelServices;
    if(items.includes(TRAVEL_SERVICES.TOUR_PACKAGE)){
      items = [TRAVEL_SERVICES.TOUR_PACKAGE];
    }
    
    items.forEach((item: any, index: number) => {
      if (invoiceItems.length <= index) {
        invoiceItems.push(this.createInvoiceItem());
      }
      invoiceItems.at(index).patchValue({
        product: item
      });
    });
  }

  patchItineraryDetails(quoteData:any){
    const invoiceItems = this.invoiceItems; 

    let noOfPeople: any;
    if(quoteData.itineraryDetails){
      noOfPeople = quoteData.itineraryDetails?.totalAdults +  quoteData.itineraryDetails?.totalChildrens;

      // Patch currency code from quote to invoice
      if(quoteData.itineraryDetails.currencyCode){
        this.invoiceForm.patchValue({
          currencyCode: quoteData.itineraryDetails.currencyCode
        })
      }
    }
    for(let i = 0; i < invoiceItems.length; i++){
      if (invoiceItems.length <= i) {
        return
      }
      invoiceItems.at(i).patchValue({
        quantity: noOfPeople,
      });
    };
  }

  patchInvoiceData(formData:any){
    // Patch releated form data
    this._leadService.getLead(formData.leadId).subscribe((data) => {
      this.leadDetails = data;
      this.patchClientdata(data);
      
      if(formData.email){
        this.invoiceForm.patchValue({clientEmail: formData.email});
      }
      if(formData.lastName){
        this.invoiceForm.patchValue({clientLastName: formData.lastName});
      }
      if(formData.clientCompanyName){
        this.invoiceForm.patchValue({clientCompanyName: formData.clientCompanyName});
      }
    })

    this.quoteId = formData.itineraryId;
    this.invoiceForm.patchValue({
      internalInvoiceId: formData.internalInvoiceId,
      currencyCode: formData.currencyCode,
      clientId: formData.leadId,
      clientFirstName: formData.firstName,
      clientLastName: formData.lastName,
      clientEmail: formData.email,
      clientCompanyName: formData.clientCompanyName,
      clientPhoneNumber: { countryCode: formData.phone.split(' ')[0], phone: formData.phone.split(' ')[1] },
      
      clientAddress: formData.address,
      hsnCode: formData.hsnCode,
      gstNumberOfClinet: formData.gstInOfCustomer,
      placeOfSupply: formData.placeOfSupply,

      // Ownership
      ownershipName: formData.ownershipName,
      ownershipPhoneNumber: { countryCode: formData.ownershipPhone.split(' ')[0], phone: formData.ownershipPhone.split(' ')[1] },
      ownershipGSTIN: formData.ownersGstin,
      ownershipOfficeEmail: formData.officalEmail,

      // Payment
      beneficiaryName: formData.accountName,
      accountNumber: formData.accountNumber,
      bankName: formData.bankName,
      ifscCode: formData.ifscCode,
      invoiceDate: this.convertToDate(formData.invoiceDate),
      dueDate: this.convertToDate(formData.dueDate),

      // Invoice items and prices
      
      isTcsApplicable: formData.isTcsApplicable,
      isGstOnTotalAmount: formData.isGstOnTotalAmount,
      totalInvoiceItemsAmount: formData.initialAmount,
      serviceAmount: formData.serviceAmount || 0,
      discountedAmount: formData.discountOffered,
      totalAmountAfterDiscount: formData.amountAfterDiscount,
      selectedGstType: formData.selectedGstType,
      igstvalue: formData.igst,
      cgstValue: formData.cgst,
      sgstValue: formData.sgst,
      tcsValue: formData.tcs || 0,
      gstAmount: formData.gst,
      tcsAmount: formData.tcsAmount || 0,
      paymentMethod: formData.paymentMethod,
      paymentReferenceNumber: formData.paymentReferenceNumber,
      amountPaid: formData.advancePaid,
      netPayable: formData.totalAmount,
      cancellationPolicy: formData.cancellationPolicy,
      latePaymentTerms: formData.latePaymentTerms,
      termsAndConditions: formData.termsAndConditions,
      notes: formData.notes,
    })
    
    if(formData.advancePaid){
      this.advanceRecieved = true
    }

    this.patchInvoiceItems(formData.invoiceItems);
  }

  patchInvoiceItems(items: any) {
    const invoiceItems = this.invoiceItems;    
    items.forEach((item: any, index: number) => {
      if (invoiceItems.length <= index) {
        invoiceItems.push(this.createInvoiceItem());
      }
      invoiceItems.at(index).patchValue({
        product: item.product,
        price: item.price,
        quantity: item.quantity,
        total: item.totalPrice
      });
    });
  }

  get today(){
    return this._globalService.getTodaysDate();
  }

  setupValueChangeSubscriptions() {
    this.invoiceItems.valueChanges.subscribe(() => this.calculateTotalInvoiceItemsAmount());
  
    this.invoiceForm.get('serviceAmount')?.valueChanges.subscribe(() => this.calculateTotalAmountAfterDiscount());
    this.invoiceForm.get('discountedAmount')?.valueChanges.subscribe(() => this.calculateTotalAmountAfterDiscount());
  
    this.invoiceForm.get('isTcsApplicable')?.valueChanges.subscribe(() => this.calculateGstAmount());
    this.invoiceForm.get('isGstOnTotalAmount')?.valueChanges.subscribe(() => this.calculateGstAmount());

    this.invoiceForm.get('igstvalue')?.valueChanges.subscribe(() => this.calculateGstAmount());
    this.invoiceForm.get('cgstValue')?.valueChanges.subscribe(() => this.calculateGstAmount());
    this.invoiceForm.get('sgstValue')?.valueChanges.subscribe(() => this.calculateGstAmount());
    this.invoiceForm.get('selectedGstType')?.valueChanges.subscribe(() => this.calculateGstAmount());  

    this.invoiceForm.get("tcsValue")?.valueChanges.subscribe(()=> this.calculateGstAmount())
  }

  readClientDataOnSelect(data:any){
    this.leadDetails = data;
    this.patchLeadDataToInvoiceItems(this.leadDetails);
    this.patchClientdata(data);
    if(data.itineraryId){
      this.getQuoteData(this.leadDetails.itineraryId);
    }
    this.invoiceForm.get(`clientId`)?.enable();
  }

  patchClientdata(clientData:any){    
    this.invoiceForm.get(`clientId`)?.setValue(clientData);
    this.invoiceForm.get('clientPhoneNumber')?.setValue({countryCode: clientData.phoneNumber.countryCode, phone: clientData.phoneNumber.phone });
    this.invoiceForm.get('clientEmail')?.setValue(clientData.email);
    this.invoiceForm.get('clientCompanyName')?.setValue(clientData.clientCompanyName);
    this.invoiceForm.get('clientFirstName')?.setValue(clientData.firstName);
    this.invoiceForm.get('clientLastName')?.setValue(clientData.lastName);

    this.invoiceForm.get(`clientId`)?.disable();
    this.invoiceForm.get('clientPhoneNumber')?.disable();
    this.invoiceForm.get('clientFirstName')?.disable();
    if(clientData.lastName){
      this.invoiceForm.get('clientLastName')?.disable();
    }else{
      this.invoiceForm.get('clientLastName')?.enable();
    }
  }

  get invoiceItems(): FormArray {
    return this.invoiceForm.get('invoiceItems') as FormArray;
  }

  createInvoiceItem(): FormGroup {
    const invoiceItem = this.fb.group({
      product: ['', Validators.required],
      price: ['', Validators.required],
      quantity: ['', Validators.required],
      total: [{ value: 0, disabled: true }]
    });
  
    invoiceItem.get('price')?.valueChanges.subscribe(() => this.calculateItemTotal(invoiceItem));
    invoiceItem.get('quantity')?.valueChanges.subscribe(() => this.calculateItemTotal(invoiceItem));
  
    return invoiceItem;
  }

  addItem() {
    this.invoiceItems.push(this.createInvoiceItem());
    this.calculateTotalInvoiceItemsAmount();
  }

  removeItem(index: number) {
    this.invoiceItems.removeAt(index);
    this.calculateTotalInvoiceItemsAmount();
  }

  // Auto calcutions on value chagnes

  calculateItemTotal(item: FormGroup) {
    const price = parseFloat(item.get('price')?.value || '0');
    const quantity = parseFloat(item.get('quantity')?.value || '0');
    const total = price * quantity;
  
    item.get('total')?.setValue(this.formatToTwoDecimals(total), { emitEvent: false });
  
    // Recalculate the total for all items whenever an item total changes
    this.calculateTotalInvoiceItemsAmount();
  }

  calculateTotalInvoiceItemsAmount() {    
    const invoiceItems = this.invoiceItems.controls;
    const totalAmount = invoiceItems.reduce((acc, item) => {
      const itemTotal = parseFloat(item.get('total')?.value || '0');
      return acc + itemTotal;
    }, 0);
    
    this.invoiceForm.get('totalInvoiceItemsAmount')?.setValue(this.formatToTwoDecimals(totalAmount), { emitEvent: false });
    this.calculateTotalAmountAfterDiscount();
  }
  
  calculateTotalAmountAfterDiscount() {
    const totalItemsAmount = this.invoiceForm.get('totalInvoiceItemsAmount')?.value || 0;
    const serviceAmount = this.invoiceForm.get('serviceAmount')?.value || 0;
    const discountedAmount = this.invoiceForm.get('discountedAmount')?.value || 0;
    const totalAfterDiscount = +(totalItemsAmount) + +(serviceAmount) - +(discountedAmount);
  
    this.invoiceForm.get('totalAmountAfterDiscount')?.setValue(this.formatToTwoDecimals(totalAfterDiscount), { emitEvent: false });
    this.calculateGstAmount();
  }
  
  calculateGstAmount() {
    const isGstOnTotalAmount =  this.invoiceForm.get('isGstOnTotalAmount')?.value;
    const gstType = this.invoiceForm.get('selectedGstType')?.value;
    const totalAfterDiscount = this.invoiceForm.get('totalAmountAfterDiscount')?.value || 0;
    const serviceAmount = this.invoiceForm.get('serviceAmount')?.value || 0;
  
    let gstAmount = 0;
    if(isGstOnTotalAmount){
      if (gstType === 'combinedGst') {
        const igstValue = this.invoiceForm.get('igstvalue')?.value || 0;
        gstAmount = (igstValue / 100) * totalAfterDiscount;
      } else if (gstType === 'seperateGst') {
        const cgstValue = this.invoiceForm.get('cgstValue')?.value || 0;
        const sgstValue = this.invoiceForm.get('sgstValue')?.value || 0;
        gstAmount = ((+(cgstValue) + +(sgstValue)) / 100) * totalAfterDiscount;
      }
    }else{
      if (gstType === 'combinedGst') {
        const igstValue = this.invoiceForm.get('igstvalue')?.value || 0;
        gstAmount = (igstValue / 100) * serviceAmount;
      } else if (gstType === 'seperateGst') {
        const cgstValue = this.invoiceForm.get('cgstValue')?.value || 0;
        const sgstValue = this.invoiceForm.get('sgstValue')?.value || 0;
        gstAmount = ((+(cgstValue) + +(sgstValue)) / 100) * serviceAmount;
      }
    }
    
  
    this.invoiceForm.get('gstAmount')?.setValue(this.formatToTwoDecimals(gstAmount), { emitEvent: false });
    this.calculateTcsAmount();
  }

  calculateTcsAmount(){
    const tcsValue = this.invoiceForm.get('tcsValue')?.value;
    const total = this.invoiceForm.get("totalAmountAfterDiscount")?.value;
    const gstAmount = this.invoiceForm.get("gstAmount")?.value;

    let tcsAmount = (tcsValue / 100) * (+total + +gstAmount)
    this.invoiceForm.get('tcsAmount')?.setValue(this.formatToTwoDecimals(tcsAmount), { emitEvent: false });
    this.calculateBalanceAmount();
  }
  
  calculateBalanceAmount() {
    const isTcsApplicable =  this.invoiceForm.get('isTcsApplicable')?.value;
    const totalAfterDiscount = this.invoiceForm.get('totalAmountAfterDiscount')?.value || 0;
    const gstAmount = this.invoiceForm.get('gstAmount')?.value || 0;
    const tcsAmount = this.invoiceForm.get('tcsAmount')?.value;
  
    let totalAmount = +totalAfterDiscount + +gstAmount;

    if(isTcsApplicable){
      totalAmount = +totalAmount + +tcsAmount;
    }
  
    this.invoiceForm.get('netPayable')?.setValue(this.formatToTwoDecimals(totalAmount), { emitEvent: false });
    this.invoiceForm.get('balanceAmountInWords')?.setValue(this.convertAmountToWords(totalAmount), { emitEvent: false });
  }
  
  convertAmountToWords(amount: number): string {
    return this._globalService.convertNumberToWords(amount) + " only";
  }

  formatToTwoDecimals(input: string | number): string {
    return this._globalService.formatToTwoDecimals(input);
  }

  toggleAdvanceRecieved(event: any): void {
    const isChecked = event.target.checked;
    this.advanceRecieved = isChecked;
    if(this.advanceRecieved){
      this.invoiceForm.get("paymentMethod")?.setValidators([Validators.required]);
      this.invoiceForm.get("amountPaid")?.setValidators([Validators.required, this.validateAdvanceAmount()]);
    }else{
      this.invoiceForm.get("paymentMethod")?.setValidators([]);
      this.invoiceForm.get("amountPaid")?.setValidators([]);
      this.invoiceForm.get('paymentMethod')?.setValue('');
      this.invoiceForm.get('amountPaid')?.setValue('');
      this.invoiceForm.get('paymentReferenceNumber')?.setValue('');
    }
    this.invoiceForm.get("paymentMethod")?.updateValueAndValidity();
    this.invoiceForm.get("amountPaid")?.updateValueAndValidity();
    this.invoiceForm.get('paymentReferenceNumber')?.updateValueAndValidity();
  }
  
  // Function to check whether input field empty
  isFieldEmptyInForm(formControlName: string): boolean {
    return this.invoiceForm.get(formControlName)?.value === "" || this.invoiceForm.get(formControlName)?.value === null || this.invoiceForm.get(formControlName)?.value === undefined;
  }
  isInvoiceItemsCellEmpty(index: number,formControlName: string): boolean {
    let invoiceItems = this.invoiceItems;
    let value = invoiceItems.at(index).get(formControlName)?.value;
    return value === "" || value === null || value === undefined;
  }

  get getCurrencyCode(){
    return this.invoiceForm.get('currencyCode')?.value;
  }
  

  onSubmit(saveAndNew: boolean) {
    if (this.invoiceForm.valid) {      
      const invoiceData = this.invoiceForm.getRawValue();
      this.serializeFormData(invoiceData).then(formData=>{
        if(this.editInvoiceId){
          this._invoiceService.updateInvoice(this.editInvoiceId,formData).subscribe((data)=>{
            if(data){
              this._responseNotificationService.showMessage("Invoice Updated Successfully!")
              this.closeModal()
              this._router.navigate([`invoice/details/${data.invoiceId}`]);
              this._reloadService.triggerInvoicesReload();
            }
          })
        }else{
          if(!this.checkNetpayableValidity(formData)){
            return
          }
          this._invoiceService.createInvoice(formData).subscribe((data)=>{
            if(data){
              this._responseNotificationService.showMessage("Invoice Added Successfully!")
              this.closeModal()
              this._router.navigate([`invoice/details/${data.invoiceId}`]);
              this._reloadService.triggerInvoicesReload();
            }
          })
        }
        
      })
    } else {
      // Handle form validation errors
      this.invoiceForm.markAllAsTouched();
      this._globalService.scrollToFirstInvalidControl();
      this._responseNotificationService.showMessage("Form is invalid, please fill all required fields.", true);
    }
  }

  checkNetpayableValidity(invoiceData:any){
    if(invoiceData.totalAmount < this.amountReceviedAgainstLead){
      this._globalService.customAlert(`Netpayable (₹${invoiceData.totalAmount}) amount should always be greater than Advance received against the lead (₹${this.amountReceviedAgainstLead}).`, "Unable to submit")
      return false;
    }else{
      return true;
    }
  }

  private async serializeFormData(formData:any){
    const serializedData = {
      internalInvoiceId: formData.internalInvoiceId,
      currencyCode: formData.currencyCode,
      leadId: formData.clientId.leadId,
      itineraryId: this.quoteId,
      firstName : formData.clientFirstName ,
      lastName : formData.clientLastName ,
      phone: formData.clientPhoneNumber.countryCode + " " + formData.clientPhoneNumber.phone,
      email : formData.clientEmail ,
      clientCompanyName : formData.clientCompanyName?.trim() || "",
      address : formData.clientAddress ,
      hsnCode: formData.hsnCode,
      gstInOfCustomer: formData.gstNumberOfClinet,
      placeOfSupply: formData.placeOfSupply,
      // ownership
      ownershipName : formData.ownershipName ,
      ownershipPhone : formData.ownershipPhoneNumber.countryCode + " " + formData.ownershipPhoneNumber.phone ,
      ownersGstin: formData.ownershipGSTIN,
      officalEmail : formData.ownershipOfficeEmail ,
      // payment fields
      accountName : formData.beneficiaryName ,
      accountNumber : formData.accountNumber ,
      bankName : formData.bankName ,
      ifscCode : formData.ifscCode ,
      invoiceDate : this._globalService.convertDateToString(formData.invoiceDate) ,
      dueDate : this._globalService.convertDateToString(formData.dueDate) ,
      // Invoice items and prices
      invoiceItems : formData.invoiceItems.map((item:any) => ({
        product: item.product,
        price: item.price,
        quantity: item.quantity,
        totalPrice: item.total
      })),
      isTcsApplicable: formData.isTcsApplicable,
      isGstOnTotalAmount: formData.isGstOnTotalAmount,
      initialAmount: formData.totalInvoiceItemsAmount,
      serviceAmount : formData.serviceAmount ,
      discountOffered : formData.discountedAmount ,
      amountAfterDiscount: formData.totalAmountAfterDiscount,
      selectedGstType : formData.selectedGstType ,
      iGst : formData.igstvalue ,
      cgst : formData.cgstValue ,
      sgst : formData.sgstValue ,
      tcs : formData.tcsValue ,
      paymentMethod: formData.paymentMethod,
      paymentReferenceNumber: formData.paymentReferenceNumber,
      advancePaid : formData.amountPaid ,
      totalAmount: formData.netPayable,
      cancellationPolicy : formData.cancellationPolicy ,
      latePaymentTerms : formData.latePaymentTerms ,
      termsAndConditions : formData.termsAndConditions ,
      notes : formData.notes,
      invoiceId: this.invoiceDetails ? this.invoiceDetails.invoiceId : null,
      organizationId: this.invoiceDetails?.organizationId,
      id: this.invoiceDetails ? this.invoiceDetails.id : null,
      ownershipRole: this.invoiceDetails ? this.invoiceDetails.ownershipRole : null,
      ownership: this.invoiceDetails ? this.invoiceDetails.ownership : null,
      category: this.invoiceDetails ? this.invoiceDetails.category : null,
      status: this.invoiceDetails ? this.invoiceDetails.status : null,
    }

    return serializedData;
  }

  closeModal() {
    this.modalController.dismiss();
  }

  // Custom validators
  validateDiscountAmount() {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control || control.value === null || control.value === undefined || control.value === '') {
        return null;
      }
      if (!this.invoiceForm) {
        return null;
      }
      const requestedAmount = Number(control.value);
      const invoiceAmount =
        Number(this.invoiceForm.get('totalInvoiceItemsAmount')?.value || 0) +
        Number(this.invoiceForm.get('serviceAmount')?.value || 0);

      if (!isNaN(requestedAmount) && !isNaN(invoiceAmount) && requestedAmount > invoiceAmount) {
        return { requestedAmountExceedsBalance: true };
      }
      return null;
    };
  }

  validateAdvanceAmount() {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control || control.value === null || control.value === undefined || control.value === '') {
        return null;
      }
      if (!this.invoiceForm) {
        return null;
      }
      const requestedAmount = Number(control.value);
      const currentBalanceAmount = Number(this.invoiceForm.get('netPayable')?.value || 0);

      if (!isNaN(requestedAmount) && !isNaN(currentBalanceAmount) && requestedAmount > currentBalanceAmount) {
        return { requestedAmountExceedsBalance: true };
      }
      return null;
    };
  }
}
